import Constants from 'expo-constants';
import pick from 'lodash/pick';
import { useCallback } from 'react';
import { Platform, PlatformOSType } from 'react-native';

import useAuth from 'app/hooks/useAuth';
import useStore, { Env } from 'app/hooks/useStore';
import { remoteConfig } from 'app/services/Firebase';
import Logger from 'app/services/Logger';
import { isVersionOutdated } from 'app/utils/version';

const MODULE = '[useFeatures]';

export type FeatureName = 'modifyReservations' | 'zendeskMessaging';

export type RemoteFeatureConfig = {
  name: string;
  enabled: boolean;
  claims: Maybe<string[]>;
  target: Maybe<PlatformOSType[]>;
  env: Maybe<Env[]>;
  minVersion: Maybe<string>;
};

export default function useFeatures() {
  const { email, userClaims } = useAuth();

  const env = useStore((s) => s.env);
  const featureEnabled = useCallback(
    (featureName: FeatureName): boolean => {
      try {
        const featConfig: unknown = JSON.parse(remoteConfig().getString(featureName));

        if (isRemoteFeatureConfig(featConfig) && featConfig.enabled === true) {
          // when we have claims defined on the feature check that they are all present on the user claims set to true
          const hasClaims = Array.isArray(featConfig.claims)
            ? featConfig.claims.every((claim) => userClaims?.[claim] === true)
            : true;
          const hasTarget = Array.isArray(featConfig.target)
            ? featConfig.target.some((t) => t === Platform.OS)
            : true;
          const hasEnv = Array.isArray(featConfig.env) ? featConfig.env.includes(env) : true;
          const meetsVersion =
            typeof featConfig.minVersion === 'string'
              ? !isVersionOutdated(Constants.expoConfig?.version, featConfig.minVersion)
              : true;

          const isEnabled = hasClaims && hasTarget && hasEnv && meetsVersion;
          Logger.info(`${MODULE} ${email} : feature ${featureName} : enabled ${isEnabled}`, {
            featConfig,
            matchingClaims: featConfig.claims ? pick(userClaims, featConfig.claims) : {},
          });

          return isEnabled;
        }
      } catch (ex) {
        Logger.info(`${MODULE} ${email} : feature ${featureName} failed`, {
          ex,
        });
      }

      return false;
    },
    [email, env, userClaims]
  );

  return { featureEnabled };
}

function isRemoteFeatureConfig(value: unknown): value is RemoteFeatureConfig {
  return value != null && typeof value === 'object' && 'enabled' in value;
}
